import Grid from '@material-ui/core/Grid'
import React, { FC } from 'react'
import { Hero } from './style'

//* images
import AIRobot from '../../../../../static/steam-img/artificial-intelligence-hero-robot.png'
import AutomationRobot from '../../../../../static/steam-img/automation-robot.png'
import DigitalTransformationRobot from '../../../../../static/steam-img/digital-transformation-robot.png'
import IndustrySolutionsRobot from '../../../../../static/steam-img/industry-solutions-hero-robot.png'
import Path from '../../../../../static/steam-img/path.png'
import ProductRobot from '../../../../../static/steam-img/product-hero-robot.png'
import RPARobot from '../../../../../static/steam-img/rpa-hero-robot.png'

//* utils
import { UseCaseTypes, secondaryBlogBreadcrumbs } from '../../../../constants/blogUseCase'
import { LocaleCodes } from '../../../../constants/localization.constants'
import { ComponentAnnouncement } from '../../../../models/components/announcement'
import { ComponentMainNavBanner } from '../../../../models/components/mainNavBanner'
import { MenuGroup } from '../../../../models/others/menuGroup'
import { generateAdData } from '../../../../utils/functions.utils'

//* components
import BlogListing from '../../../global/blogListing'
import Breadcrumbs from '../../../global/breadcrumbs'
import GlobalContainer from '../../../global/globalContainer'
import Typography from '../../../global/typography'
import WrapperImage from '../../../global/wrapperImage'
import Layout from '../../../layouts/layout'
import BlogNavigation from '../navigation'

type HeroProp = {
  categoryName: string
  categoryDescription: string
  useCase: string
  isSecondaryBlog: boolean
  locale: string
}

type ListingProp = {
  pageContext: any
  headerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  footerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  announcementData?: ComponentAnnouncement
  mainNavBannerData?: ComponentMainNavBanner
  browserLanguageData?: any
}

const CategoryHero: FC<HeroProp> = ({
  categoryName,
  categoryDescription,
  useCase,
  locale,
  isSecondaryBlog,
}) => {
  const categoryNameToImg: any = {
    'Product and Updates': ProductRobot,
    'Robotic Process Automation (RPA)': RPARobot,
    'Industry Solutions': IndustrySolutionsRobot,
    'Artificial Intelligence (AI)': AIRobot,
    'Digital Transformation': DigitalTransformationRobot,
    Automation: AutomationRobot,
  }

  const randomRobot = [...Object.values(categoryNameToImg)][
    (Math.random() * Object.values(categoryNameToImg).length) | 0
  ]

  const robotImg = categoryNameToImg[categoryName] || randomRobot

  let breadcrumbsPath = ''
  if (useCase) {
    breadcrumbsPath = `${
      useCase === UseCaseTypes.MAIN_BLOG ? '/blog' : secondaryBlogBreadcrumbs[useCase]
    }`
  } else {
    breadcrumbsPath = '/blog'
  }

  return (
    <Hero>
      <div className="Hero__Outer-Container">
        <GlobalContainer>
          <Breadcrumbs
            backgroundTheme="light"
            currentPathname={breadcrumbsPath}
            pageName={categoryName}
            className="Hero__Breadcrumb"
            isBlogBreadcrumb={true}
            locale={locale}
            isSecondaryBlog={isSecondaryBlog}
          />

          <Grid container className="Hero__Inner-Container" spacing={2}>
            <Grid item className="Hero__Text-Container" sm={6} md={6} lg={5}>
              <Typography type="header1">{categoryName}</Typography>

              <Typography className="Hero__Body font__inter" type="body">
                {categoryDescription}
              </Typography>
            </Grid>

            <div className="Hero__Image-Container">
              <WrapperImage
                className="Hero__Image-Robot"
                src={robotImg}
                alt={`${categoryName} Robot`}
                width={384}
                height={328}
                threshold={650}
              />

              <WrapperImage
                className="Hero__Image-Path"
                src={Path}
                alt="Half moon icon"
                width={678}
                height={319}
                threshold={650}
              />
            </div>
          </Grid>
        </GlobalContainer>
      </div>
    </Hero>
  )
}

const CategoryListings: FC<ListingProp> = ({
  pageContext,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}) => {
  if (!pageContext.pageData) return null

  const categoryListingData = {
    useCase: pageContext.pageData.useCase || '',
    isSecondaryBlog: pageContext.pageData.isSecondaryBlog || false,
    locale: pageContext.pageData.locale || LocaleCodes.ENGLISH.code,
    featuredResource: pageContext.blogFeaturedResource || null,
    blogNav: pageContext.pageData.blogNav || null,
    path: pageContext.pageData.path || '/blog',
    blogs: pageContext.pageData.blogs || [],
    pageNumber: pageContext.pageData.pageNumber || 0,
    totalPages: pageContext.pageData.totalPages || 0,
    totalListingPages: pageContext.pageData.totalListingPages || 0,
    slug: pageContext.pageData.slug || '',
    subscribe: pageContext.pageData.subscribe || null,
    seoMetadata: pageContext.pageData.seoMetadata || null,
    collectionName: pageContext.pageData.collectionName || '',
    categoryDescription: pageContext.pageData.categoryDescription || '',
  }

  const getFeaturedAd = () => {
    if (categoryListingData.featuredResource) {
      return generateAdData(categoryListingData.featuredResource)
    }
  }

  return (
    <Layout.Template
      lang={categoryListingData.locale}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}>
      <div data-cy="CategoryListing">
        {categoryListingData.blogNav && (
          <BlogNavigation
            blogNav={categoryListingData.blogNav}
            pagePath={categoryListingData.path}
            useCase={categoryListingData.useCase}
          />
        )}

        {categoryListingData.blogs.length && (
          <>
            {categoryListingData.pageNumber === 1 &&
              categoryListingData.collectionName &&
              categoryListingData.categoryDescription &&
              CategoryHero({
                categoryName: categoryListingData.collectionName,
                categoryDescription: categoryListingData.categoryDescription,
                useCase: categoryListingData.useCase,
                locale: categoryListingData.locale,
                isSecondaryBlog: categoryListingData.isSecondaryBlog,
              })}

            <BlogListing
              posts={categoryListingData.blogs}
              totalPages={categoryListingData.totalPages}
              pageNumber={categoryListingData.pageNumber}
              seoData={categoryListingData.seoMetadata}
              slug={categoryListingData.slug}
              subscribe={categoryListingData.subscribe}
              numListingPages={categoryListingData.totalListingPages}
              blogFRData={getFeaturedAd()}
              locale={categoryListingData.locale}
              disableListingTitle={categoryListingData.useCase !== UseCaseTypes.MAIN_BLOG} // "latest article" not needed for secondary blog
              isMainBlog={categoryListingData.useCase === UseCaseTypes.MAIN_BLOG}
            />
          </>
        )}
      </div>
    </Layout.Template>
  )
}

export default CategoryListings
